import type { LucideIcon } from "lucide-react";
import { ArrowRight, ChevronRight, RefreshCw } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tile } from "@/components/ui/tile";
import { cn } from "@/lib/utils";
import { WORKSPACE_WEIGHT_GRAMMAR } from "./workspace-weight-grammar";

export type WorkspaceWeight = keyof typeof WORKSPACE_WEIGHT_GRAMMAR;

export interface WorkspaceAction {
  label: string;
  href: string;
  reason: string;
}

/** The one server-ranked action. Labels, hrefs, and reasons pass through unchanged. */
export function PrimaryCourseAction({ action }: { action: WorkspaceAction }) {
  return (
    <Button asChild size="lg" className="w-full sm:w-auto">
      <Link to={action.href} data-course-home-primary={action.reason}>
        {action.label}
        <ArrowRight className="size-4" strokeWidth={1.75} aria-hidden="true" />
      </Link>
    </Button>
  );
}

export function SecondaryCourseAction({ href, children, accessibleLabel }: {
  href: string;
  children: React.ReactNode;
  accessibleLabel?: string;
}) {
  return (
    <Button asChild size="sm" variant="outline" className="w-full sm:w-auto">
      <Link to={href} aria-label={accessibleLabel}>{children}</Link>
    </Button>
  );
}

/**
 * The screen's one academic object: identity, context, then action. One tile
 * (ADR 0029): the identity row, then the context and the act after one rule.
 */
export function LeadCard({
  id,
  eyebrow,
  title,
  children,
  action,
  className,
  stratum,
}: {
  id: string;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  stratum?: "lead";
}) {
  return (
    <section aria-labelledby={id} data-course-home-stratum={stratum}>
      <Tile
        as="div"
        data-workspace-weight="lead"
        className={cn("gap-4", className)}
      >
        <div data-record-region="identity" className="grid min-w-0 gap-1">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h2 id={id} className="text-tile-title font-semibold text-foreground">
            {title}
          </h2>
        </div>
        {(children || action) && (
          <div className="grid min-w-0 gap-5 border-t border-rule pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            {children && <div data-record-region="context" className="min-w-0">{children}</div>}
            {action && <div data-record-region="action" className="min-w-0">{action}</div>}
          </div>
        )}
      </Tile>
    </section>
  );
}

/** The role's open work region: an eyebrow, one rule, then unboxed rows. */
export function SupportSection({
  id,
  label,
  description,
  children,
  className,
  stratum,
}: {
  id: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  stratum?: "support";
}) {
  return (
    <section
      data-workspace-weight="support"
      data-course-home-stratum={stratum}
      aria-labelledby={id}
      className={cn("grid gap-3", className)}
    >
      <div className="grid gap-1">
        <p id={id} className="eyebrow">{label}</p>
        {description && <p className="max-w-2xl text-ui text-muted-foreground">{description}</p>}
      </div>
      <div className="border-t border-rule">{children}</div>
    </section>
  );
}

/** Healthy or empty context in one unboxed line, optionally with disclosure. */
export function QuietLine({
  children,
  disclosure,
  announce = false,
  className,
}: {
  children: React.ReactNode;
  disclosure?: { label: string; children: React.ReactNode; defaultOpen?: boolean };
  announce?: boolean;
  className?: string;
}) {
  return (
    <div
      role={announce ? "status" : undefined}
      data-workspace-weight="quiet"
      className={cn("text-ui text-muted-foreground", className)}
    >
      <span>{children}</span>
      {disclosure && (
        <DisclosureLine
          label={disclosure.label}
          defaultOpen={disclosure.defaultOpen}
          className="mt-1"
        >
          {disclosure.children}
        </DisclosureLine>
      )}
    </div>
  );
}

/** Keyboard-native, sentence-shaped disclosure using governed motion. */
export function DisclosureLine({
  label,
  children,
  defaultOpen = false,
  className,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      data-workspace-weight="disclosure"
      className={cn("group/disclosure", className)}
    >
      <CollapsibleTrigger className="inline-flex min-h-11 max-w-full items-center gap-2 text-left text-ui text-muted-foreground underline-offset-4 transition-colors duration-[var(--motion-fast)] hover:text-foreground hover:underline focus-visible:rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronRight
          aria-hidden="true"
          data-workspace-disclosure-icon
          strokeWidth={1.75}
          className="size-4 shrink-0 transition-transform group-data-[state=open]/disclosure:rotate-90"
          style={{
            transitionDuration: "var(--motion-fast)",
            transitionTimingFunction: "var(--ease-standard)",
          }}
        />
        <span>{label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden pl-6 text-ui text-muted-foreground">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A failed read withdraws its claim in one announced sentence. */
export function WithdrawnNotice({
  children,
  onRetry,
  retryLabel = "Try again",
  accessibleLabel,
  className,
}: {
  children: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  accessibleLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label={accessibleLabel}
      data-workspace-weight="withdrawn"
      className={cn("grid justify-items-start gap-3 text-ui text-muted-foreground", className)}
    >
      <span>{children}</span>
      {onRetry && (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="size-4" strokeWidth={1.75} aria-hidden="true" /> {retryLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * One exact record waiting for work. The server's identity and order pass
 * through; matching the primary href removes the duplicate row action.
 */
export function WorkQueueRow({
  identity,
  evidence,
  state,
  action,
  primaryAction,
  primaryActionHref,
  recordId,
  attemptNumber,
}: {
  identity: React.ReactNode;
  evidence: React.ReactNode;
  state?: React.ReactNode;
  action?: { href: string; label: string; accessibleLabel?: string };
  /** When this exact row owns the ranked action, render it here, once. */
  primaryAction?: WorkspaceAction | null;
  primaryActionHref: string | null;
  recordId?: string;
  attemptNumber?: number;
}) {
  return (
    <article
      className="grid min-w-0 gap-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
      data-workspace-work-row
      data-workspace-attention-row
      data-record-id={recordId}
      data-attempt-number={attemptNumber}
    >
      <div className="grid min-w-0 gap-1">
        <p className="text-body font-medium text-foreground">{identity}</p>
        <p className="text-ui text-muted-foreground">{evidence}</p>
        {state && <p className="text-ui text-foreground">{state}</p>}
      </div>
      {action && (
        primaryAction?.href === action.href
          ? <PrimaryCourseAction action={primaryAction} />
          : primaryActionHref !== action.href
            ? <SecondaryCourseAction href={action.href} accessibleLabel={action.accessibleLabel}>{action.label}</SecondaryCourseAction>
            : null
      )}
    </article>
  );
}

/* Compatibility adapters keep existing call sites stable until each canvas's
   authorized recomposition. The icon prop remains type-only and is not
   rendered, so the retired icon-circle voice cannot survive through it. */
export function WorkspaceSectionHeading({
  id,
  eyebrow,
  children,
}: {
  id: string;
  icon: LucideIcon;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h2 id={id} className="text-section font-semibold text-foreground">{children}</h2>
    </div>
  );
}

export function AttentionRow({
  title,
  detail,
  action,
  primaryActionHref,
}: {
  title: React.ReactNode;
  detail: React.ReactNode;
  action: { href: string; label: string; accessibleLabel?: string };
  primaryActionHref: string | null;
}) {
  return (
    <WorkQueueRow
      identity={title}
      evidence={detail}
      action={action}
      primaryActionHref={primaryActionHref}
    />
  );
}

/** Calm read-only context for course rhythm and resources. */
export function ContextRow({
  label,
  title,
  detail,
  className,
}: {
  label?: string;
  title: React.ReactNode;
  detail?: React.ReactNode;
  className?: string;
}) {
  return (
    <article className={cn("grid min-w-0 gap-1 py-4", className)}>
      {label && <div className="eyebrow">{label}</div>}
      <p className="text-body font-medium text-foreground">{title}</p>
      {detail && <p className="text-ui text-muted-foreground">{detail}</p>}
    </article>
  );
}

/** Immutable learner evidence. This surface contains no controls. */
export function EvidenceReceipt({ children }: { children: React.ReactNode }) {
  return (
    <dl
      className="grid min-w-0 gap-x-6 gap-y-3 border-t border-rule pt-4 text-ui sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]"
      data-workspace-evidence="immutable"
    >
      {children}
    </dl>
  );
}

export function WorkspaceEmptyState({ children }: { children: React.ReactNode }) {
  return <QuietLine>{children}</QuietLine>;
}

export function WorkspaceUnavailableState({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <WithdrawnNotice accessibleLabel={`${title} unavailable`} onRetry={onRetry}>
      {title} unavailable. {description}
    </WithdrawnNotice>
  );
}
